import { css } from 'glamor';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { fetchCreditCardTokens, createCreditCardToken } from '../../processes/creditCardTokenProcesses';
import {
  fetchSubscriptions,
  resumeSubscription,
  fetchResumeStatus,
  removeSubscription,
} from '../../processes/subscriptionProcesses';
import Button from '../library/Button';
import ResumeSubscriptionModal from './ResumeSubscriptionModal';

// A recusa assíncrona chega em ~3 s em produção; 60 s cobre análises mais
// lentas sem prender o aluno. Passado isso, o modal mostra "em análise".
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 20;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Mesmo formato de erro do superagent, que o modal já sabe exibir.
const chargeFailedError = (errors) => {
  const error = new Error('first charge failed');
  error.response = { body: { errors } };
  return error;
};

const renewButtonClass = css({
  fontFamily: 'Montserrat !important',
  fontSize: '12px !important',
  fontWeight: '500 !important',
});

function ResumeSubscriptionButtonContainer({ dispatch, subscription, creditCardTokens }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [underReview, setUnderReview] = useState(false);
  // Identifica a execução de polling ativa: fechar o modal (ou desmontar)
  // incrementa e invalida qualquer laço em andamento, mesmo que o modal seja
  // reaberto antes de o laço antigo acordar.
  const pollRunRef = useRef(0);

  useEffect(() => () => { pollRunRef.current += 1; }, []);

  useEffect(() => {
    if (subscription?.canResume) {
      fetchCreditCardTokens(dispatch);
    }
  }, [dispatch, subscription?.canResume]);

  const handleOpenModal = useCallback(() => setModalOpen(true), []);

  const navigateToSubscriptions = useCallback(() => {
    dispatch(push('/finance/subscriptions'));
  }, [dispatch]);

  // Recarrega a lista ao fechar. Com o modal aberto o refetch não pode
  // acontecer: ele liga o `loading` do painel, que troca a lista por um
  // spinner e desmonta o card da original — e este modal, que mora nele.
  const handleCloseModal = useCallback(() => {
    pollRunRef.current += 1;
    setModalOpen(false);
    setDeferred(null);
    setProcessing(false);
    setUnderReview(false);
    fetchSubscriptions(dispatch);
    navigateToSubscriptions();
  }, [dispatch, navigateToSubscriptions]);

  // Consulta a tentativa até a 1ª cobrança sair de "processando". Resolve com
  // 'paid', 'timeout' ou 'closed' (o aluno fechou o modal); rejeita na recusa.
  const waitForFirstCharge = useCallback(async (paymentId) => {
    pollRunRef.current += 1;
    const runId = pollRunRef.current;

    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
      await sleep(POLL_INTERVAL_MS);
      if (pollRunRef.current !== runId) return 'closed';

      let body;
      try {
        body = await fetchResumeStatus(subscription?.id, paymentId);
      } catch {
        continue; // falha de rede pontual: tenta de novo no próximo ciclo
      }

      if (body?.status === 'paid') return 'paid';
      if (body?.status === 'failed') throw chargeFailedError(body.errors);
    }

    return 'timeout';
  }, [subscription?.id]);

  const handleConfirm = useCallback((cardId) => {
    setLoading(true);
    return resumeSubscription(dispatch, subscription?.id, { cardId })
      .then(async (response) => {
        const isDeferred = response?.body?.deferred;
        const firstChargeAt = response?.body?.firstChargeAt;

        if (isDeferred) {
          setDeferred({ firstChargeAt }); // a lista é recarregada ao fechar
          return;
        }

        // Antes o modal fechava aqui mesmo com a cobrança ainda em análise, e
        // uma recusa segundos depois deixava o aluno achando que renovou.
        if (response?.body?.firstChargeStatus === 'processing') {
          setProcessing(true);
          let outcome;
          try {
            outcome = await waitForFirstCharge(response.body.payment?.id);
          } catch (error) {
            // Recusada: o backend já apagou a assinatura nova; tira ela do store
            // e a original volta. Sem refetch aqui: ele liga o `loading` do
            // painel, que desmonta a lista — e este modal junto, levando a
            // mensagem de erro. A lista é recarregada ao fechar.
            removeSubscription(dispatch, response.body.data?.id);
            throw error;
          } finally {
            setProcessing(false);
          }

          if (outcome === 'timeout') {
            setUnderReview(true);
            return;
          }
          if (outcome === 'closed') return;
        }

        fetchSubscriptions(dispatch);
        setModalOpen(false);
        navigateToSubscriptions();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, subscription?.id, navigateToSubscriptions, waitForFirstCharge]);

  const handleNewCardSubmit = useCallback((cardData) => {
    setLoading(true);
    return createCreditCardToken(dispatch, cardData)
      .then((token) => handleConfirm(token.cardId))
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, handleConfirm]);

  if (!subscription?.canResume) return null;

  return (
    <>
      <Button primary className={renewButtonClass} onClick={handleOpenModal}>
        Renovar assinatura
      </Button>

      <ResumeSubscriptionModal
        open={modalOpen}
        onClose={handleCloseModal}
        creditCardTokens={creditCardTokens}
        onConfirm={handleConfirm}
        onNewCardSubmit={handleNewCardSubmit}
        loading={loading}
        subscription={subscription}
        deferred={deferred}
        processing={processing}
        underReview={underReview}
        showSetAsDefault={subscription?.paymentService === 'pagarme'}
      />
    </>
  );
}

function mapStateToProps(state, { subscription }) {
  const allTokens = state.entities.creditCardTokens || {};
  const creditCardTokens = Object.values(allTokens).filter(
    (token) => token.paymentService === subscription?.paymentService,
  );

  return { creditCardTokens };
}

export default connect(mapStateToProps)(ResumeSubscriptionButtonContainer);
